import type { IMachineSignedEntry } from '@featit/shared/machine-grants';
import { deviceLocal } from '@/privacy/deviceStorage';

/**
 * F-0683 MP-43 (`docs/concept-machine-permissions.md` §7.3; the dispatcher's rulings, 2026-11-02): the
 * narrowings a machine applied straight from this device that the deployment has not taken yet. "The
 * sending device keeps it and relays it again once the deployment answers", so they outlive a reload
 * or a closed tab:
 *
 * - **Per deployment and person**: one key each, so two accounts in one browser never see each
 *   other's, and every key goes at sign-out.
 * - **The signed entry only**, once per entry id, dropped as soon as the deployment has it.
 * - **Bounded**: at most [`MAX_PENDING_DIRECT_ENTRIES`] per person, and none signed longer ago than
 *   [`PENDING_DIRECT_ENTRY_MAX_AGE_SECONDS`]; the machine posts each with its acknowledgement anyway.
 * - **Never failing the action**: with no storage (a private window, a write that throws), they are
 *   kept in memory for this page.
 *
 * Kept apart from the direct sender (`machineDirectEntry.ts`) so sign-out reaches it without loading
 * any machine connection code.
 */
export interface IDirectEntryOwner {
  /** The deployment the person is signed in to, as this device addresses it. */
  deployment: string;
  userId: string;
}

export const PENDING_DIRECT_ENTRIES_PREFIX = 'featit:machines:direct-entries:';

/** At most this many entries wait per person, the newest kept. */
export const MAX_PENDING_DIRECT_ENTRIES = 32;

/** An entry signed longer ago than this is no longer relayed from here: a week. */
export const PENDING_DIRECT_ENTRY_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

type PendingType = Record<string, IMachineSignedEntry[]>;

/** What this page keeps when storage keeps nothing, and the copy every write leaves. */
const memory = new Map<string, PendingType>();

function keyOf(owner: IDirectEntryOwner): string {
  return `${PENDING_DIRECT_ENTRIES_PREFIX}${encodeURIComponent(owner.deployment)}:${owner.userId}`;
}

function read(owner: IDirectEntryOwner): PendingType {
  const key = keyOf(owner);
  try {
    const raw = deviceLocal.getItem(key);
    if (raw === null) return memory.get(key) ?? {};
    const kept: unknown = JSON.parse(raw);
    return typeof kept === 'object' && kept !== null && !Array.isArray(kept)
      ? (kept as PendingType)
      : {};
  } catch {
    return memory.get(key) ?? {};
  }
}

/** The entries still worth relaying: recent enough, newest last, at most the cap across machines. */
function bounded(pending: PendingType, nowSeconds: number): PendingType {
  const oldest = nowSeconds - PENDING_DIRECT_ENTRY_MAX_AGE_SECONDS;
  const all = Object.entries(pending)
    .flatMap(([machineId, entries]) =>
      (Array.isArray(entries) ? entries : []).map((entry) => ({ machineId, entry })),
    )
    .filter(({ entry }) => entry?.record?.signedAt >= oldest)
    .sort((a, b) => a.entry.record.signedAt - b.entry.record.signedAt)
    .slice(-MAX_PENDING_DIRECT_ENTRIES);
  const kept: PendingType = {};
  for (const { machineId, entry } of all) (kept[machineId] ??= []).push(entry);
  return kept;
}

function write(owner: IDirectEntryOwner, pending: PendingType, nowSeconds: number): void {
  const key = keyOf(owner);
  const kept = bounded(pending, nowSeconds);
  memory.set(key, kept);
  try {
    // *Mutation:* keep it in memory only → a remount on a fresh page forgets it: red.
    if (Object.keys(kept).length === 0) deviceLocal.removeItem(key);
    else deviceLocal.setItem(key, JSON.stringify(kept));
  } catch {
    // No storage here: the page's own copy stands, and the machine's acknowledgement still brings it.
  }
}

const nowSeconds = () => Math.floor(Date.now() / 1000);

export function pendingDirectEntries(
  owner: IDirectEntryOwner,
  machineId: string,
  now: number = nowSeconds(),
): readonly IMachineSignedEntry[] {
  return bounded(read(owner), now)[machineId] ?? [];
}

/** Keeps `entry` for `machineId`, once per entry id. */
export function keepDirectEntry(
  owner: IDirectEntryOwner,
  machineId: string,
  entry: IMachineSignedEntry,
  now: number = nowSeconds(),
): void {
  const pending = read(owner);
  const entries = (pending[machineId] ?? []).filter(
    (each) => each.record.entryId !== entry.record.entryId,
  );
  write(owner, { ...pending, [machineId]: [...entries, entry] }, now);
}

/** Forgets the entries of `machineId` the deployment has: relayed now, or already in its ledger. */
export function settleDirectEntries(
  owner: IDirectEntryOwner,
  machineId: string,
  entryIds: Iterable<string>,
  now: number = nowSeconds(),
): void {
  const taken = new Set(entryIds);
  const pending = read(owner);
  const entries = pending[machineId] ?? [];
  if (!entries.some((entry) => taken.has(entry.record.entryId))) return;
  write(
    owner,
    { ...pending, [machineId]: entries.filter((entry) => !taken.has(entry.record.entryId)) },
    now,
  );
}

/** A new page: this one's own copy is gone, and only what storage kept remains (the tests' reload). */
export function startNewPageForPendingDirectEntries(): void {
  memory.clear();
}

/** Sign-out: every person's pending entries on this device go, with the page's own copy. */
export function forgetPendingDirectEntries(): void {
  memory.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < deviceLocal.length; index += 1) {
      const key = deviceLocal.key(index);
      if (key?.startsWith(PENDING_DIRECT_ENTRIES_PREFIX)) keys.push(key);
    }
    for (const key of keys) deviceLocal.removeItem(key);
  } catch {
    // Nothing kept.
  }
}
